// DEPLOY: /var/www/woody.wtf/frontend/src/pages/MoverAudit.js
// Audit permanent US live-mover alerts against actual EOD closes; trigger rays are view-only.
import React, { useEffect, useMemo, useState } from 'react';
import Navbar from '../components/Navbar';
import Chart2 from './Chart2';
import api from '../api';

const nyTime = iso => new Date(iso).toLocaleTimeString('en-AU', {
  timeZone:'America/New_York', hour:'2-digit', minute:'2-digit', hour12:false,
});
const nyStamp = iso => iso ? new Date(iso).toLocaleString('en-AU', {
  timeZone: 'America/New_York', month: 'short', day: '2-digit', year: 'numeric',
  hour: '2-digit', minute: '2-digit', hour12: false,
}) + ' ET' : '—';
const price = v => v == null ? '—' : '$' + Number(v).toLocaleString('en-AU', {
  minimumFractionDigits: Number(v) < 1 ? 4 : 2, maximumFractionDigits: Number(v) < 1 ? 4 : 2,
});
const pct = v => v == null ? '—' : (v > 0 ? '+' : '') + Number(v).toFixed(2) + '%';
const endMove = a => a.eod_close != null && a.price > 0 ? (a.eod_close / a.price - 1) * 100 : null;
const successColour = a => {
  const d = endMove(a);
  return d == null ? 'var(--text-muted)' : d * (a.direction === 'down' ? -1 : 1) >= 0 ? 'var(--success)' : 'var(--error)';
};

const CSS = `
.ma-page {min-height:100vh;background:var(--bg);color:var(--text)}
.ma-body {padding:22px 26px;max-width:1500px;margin:auto}
.ma-controls {display:flex;flex-wrap:wrap;align-items:end;gap:12px;margin:12px 0 18px}
.ma-controls label {display:flex;flex-direction:column;gap:4px;color:var(--text-muted);font-size:12px}
.ma-controls input {background:var(--surface);color:var(--text);border:1px solid var(--border);border-radius:var(--radius);padding:8px}
.ma-button {background:var(--surface);color:var(--text);border:1px solid var(--border);border-radius:var(--radius);padding:8px 12px;cursor:pointer}
.ma-button:disabled {opacity:.45;cursor:default}
.ma-primary {background:var(--accent);color:var(--on-accent);border-color:var(--accent)}
.ma-muted {font-size:12px;color:var(--text-muted)}
.ma-card {background:var(--surface);border:1px solid var(--border);border-radius:var(--radius);overflow:hidden}
.ma-tablewrap {overflow-x:auto}
.ma-table {width:100%;border-collapse:collapse;font-size:12px;white-space:nowrap}
.ma-table th {font-size:11px;color:var(--text-muted);font-weight:600;text-align:left;background:var(--surface)}
.ma-table th,.ma-table td {padding:11px 10px;border-bottom:1px solid var(--border)}
.ma-table td.num,.ma-table th.num {text-align:right;font-family:'JetBrains Mono',monospace}
.ma-table tbody tr:hover {background:var(--bg)}
.ma-table tbody tr.checked {background:var(--accent-dim)}
.ma-tabs {display:flex;gap:4px;overflow-x:auto;flex:1;min-width:0}
.ma-tab {white-space:nowrap;flex-shrink:0}
.ma-workspace {height:100dvh;display:flex;flex-direction:column;overflow:hidden;background:var(--bg)}
.ma-workhead {display:flex;align-items:center;gap:10px;padding:10px 16px;flex-wrap:wrap;border-bottom:1px solid var(--border)}
.ma-alerts {display:flex;flex-wrap:wrap;gap:7px;padding:8px 16px;max-height:110px;overflow-y:auto;background:var(--surface);border-bottom:1px solid var(--border);flex-shrink:0}
.ma-alert-tag {border:1px solid var(--border);border-radius:var(--radius);padding:5px 8px;font-size:12px}
.ma-charts {flex:1;min-height:0;position:relative}
.ma-chart {position:absolute;inset:0;display:flex;flex-direction:column;min-height:0}
.ma-summary {display:flex;flex-wrap:wrap;gap:10px 22px;padding:8px 16px;background:var(--surface);border-bottom:1px solid var(--border);font-size:12px;flex-shrink:0}
@media(max-width:768px) {
  .ma-body {padding:14px 10px}
  .ma-workhead {padding:8px;gap:6px}
  .ma-summary {padding:7px 10px;gap:4px 14px}
  .ma-alerts {padding:7px 10px}
  .ma-table th,.ma-table td {padding:10px 7px}
}
`;

export default function MoverAudit() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [rows, setRows] = useState([]);
  const [before, setBefore] = useState(null);
  const [checked, setChecked] = useState({});
  const [opened, setOpened] = useState(null);
  const [active, setActive] = useState(null);
  const [oldestFirst, setOldestFirst] = useState(true);
  const [firstOnly, setFirstOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [moreLoading, setMoreLoading] = useState(false);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { document.title = 'Live Mover Audit | chart.ninja'; }, []);
  useEffect(() => {
    let current = true;
    setLoading(true); setRows([]); setChecked({}); setBefore(null); setError('');
    api.get('/chart/mover-audit', { params: { from: from || undefined, to: to || undefined,
      first_only: firstOnly ? 1 : undefined } })
      .then(r => { if (current) { setRows(r.data.alerts || []); setBefore(r.data.before); } })
      .catch(e => { if (current) setError(e.response?.data?.error || 'Could not load live mover alerts'); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [from, to, firstOnly]);

  const loadOlder = async () => {
    if (!before || moreLoading) return;
    setMoreLoading(true); setError('');
    try {
      const r = await api.get('/chart/mover-audit', { params: {
        from: from || undefined, to: to || undefined, before,
        first_only: firstOnly ? 1 : undefined,
      } });
      setRows(old => [...old, ...(r.data.alerts || [])]);
      setBefore(r.data.before);
    } catch (e) { setError(e.response?.data?.error || 'Could not load older alerts'); }
    finally { setMoreLoading(false); }
  };

  const sorted = useMemo(() => [...rows].sort((a, b) =>
    (new Date(a.fired_at) - new Date(b.fired_at) || a.id - b.id) * (oldestFirst ? 1 : -1)
  ), [rows, oldestFirst]);
  const selected = useMemo(() => rows.filter(a => checked[a.id]), [rows, checked]);
  const groups = useMemo(() => {
    const unique = new Map();
    for (const a of selected) {
      const key = a.ticker + '|' + a.day;
      if (!unique.has(key)) unique.set(key, { key, ticker:a.ticker, day:a.day });
    }
    return [...unique.values()].sort((a, b) => a.day.localeCompare(b.day) || a.ticker.localeCompare(b.ticker));
  }, [selected]);

  const open = async () => {
    if (!groups.length || opening) return;
    setOpening(true); setError('');
    try {
      const entries = [];
      for (let i = 0; i < groups.length; i += 4) {
        const batch = await Promise.all(groups.slice(i, i + 4).map(async g => {
          const r = await api.get('/chart/mover-audit', { params: { ticker:g.ticker, day:g.day } });
          return { ...g, alerts:(r.data.alerts || []).map((a, j) => ({ ...a, number:j + 1 })) };
        }));
        entries.push(...batch);
      }
      const valid = entries.filter(g => g.alerts.length);
      if (!valid.length) throw new Error('No alerts found for selected charts');
      setOpened(valid);
      setActive(valid[0].key);
    } catch (e) { setError(e.response?.data?.error || e.message || 'Could not load chart alerts'); }
    finally { setOpening(false); }
  };
  const current = opened?.find(g => g.key === active) || opened?.[0];
  const first = current?.alerts[0];

  return (
    <>
      <style>{CSS}</style>
      {opened ? (
        <div className="ma-workspace">
          <Navbar compact />
          <div className="ma-workhead">
            <button className="ma-button" onClick={() => { setOpened(null); setActive(null); }}>← Alerts</button>
            <div className="ma-tabs">
              {opened.map(g => (
                <button key={g.key} className="ma-button ma-tab" onClick={() => setActive(g.key)}
                  style={g.key === active ? { borderColor:'var(--accent)',color:'var(--accent)',fontWeight:700 } : {}}>
                  {g.ticker} · {g.day} ({g.alerts.length})
                </button>
              ))}
            </div>
          </div>
          {first && (
            <>
              <div className="ma-summary">
                <span><b>{current.ticker}</b> · {current.day} · {current.alerts.length} alerts</span>
                <span>First trigger <b>{price(first.price)}</b> at {nyTime(first.fired_at)} ET · {first.direction}</span>
                <span>First → {first.session === 'post' ? 'next session close' : 'EOD'}:
                  {' '}<b>{price(first.eod_close)}</b>{first.eod_day ? ' (' + first.eod_day + ')' : ' (pending)'}</span>
                <span style={{ color:successColour(first) }}>Close vs first: <b>{pct(endMove(first))}</b></span>
              </div>
              <div className="ma-alerts" aria-label="All alerts numbered by time">
                {current.alerts.map(a => (
                  <span key={a.id} className="ma-alert-tag" style={{ borderColor:a.direction === 'down' ? 'var(--error)' : 'var(--success)' }}
                    title={'Close: ' + price(a.eod_close) + (a.eod_day ? ' on ' + a.eod_day : ' pending')}>
                    <b>#{a.number} {a.direction === 'down' ? '▼' : '▲'}</b> {nyTime(a.fired_at)} ET · {price(a.price)}
                    {' · '}<span style={{ color:successColour(a) }}>{pct(endMove(a))} to close</span>
                  </span>
                ))}
              </div>
              <div className="ma-muted" style={{ padding:'4px 16px' }}>Daily candles: numbered rays show trigger prices; individual alerts share the day’s candle position.</div>
            </>
          )}
          <div className="ma-charts">
            {current && (
              <div className="ma-chart">
                <Chart2 key={current.key} embedded initialTicker={current.ticker} auditMark={current.alerts} />
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="ma-page">
          <Navbar />
          <div className="ma-body">
            <h2 style={{ margin:'0 0 6px' }}>Live Market Movers · Audit</h2>
            <p className="ma-muted">US live alerts in time order. Select alerts to open one chart per ticker and New York day,
              with every alert on that day numbered in trigger order. First alert only filters the list to the earliest
              trigger per ticker/day/direction; charts still show all triggers.
              Close comparisons use recorded daily bars (after-hours: next session).</p>
            <div className="ma-controls">
              <label>From (NY date)<input type="date" value={from} onChange={e => setFrom(e.target.value)} /></label>
              <label>To (NY date)<input type="date" value={to} onChange={e => setTo(e.target.value)} /></label>
              <button className="ma-button" onClick={() => setOldestFirst(x => !x)}>
                {oldestFirst ? 'Oldest first ↑' : 'Newest first ↓'}
              </button>
              <button className="ma-button" aria-pressed={firstOnly}
                title="Keep the earliest alert for each ticker, New York day and direction (up/down)"
                style={firstOnly ? { borderColor:'var(--accent)',color:'var(--accent)' } : {}}
                onClick={() => setFirstOnly(x => !x)}>
                First alert only: {firstOnly ? 'On' : 'Off'}
              </button>
              <button className="ma-button" disabled={!rows.length} onClick={() => setChecked(Object.fromEntries(rows.map(a => [a.id, true])))}>
                Select all loaded
              </button>
              <button className="ma-button" disabled={!selected.length} onClick={() => setChecked({})}>Clear selection</button>
              <button className="ma-button ma-primary" disabled={!groups.length || opening} onClick={open}>
                {opening ? 'Opening…' : 'Open ' + groups.length + ' chart' + (groups.length === 1 ? '' : 's')}
              </button>
            </div>
            <div className="ma-muted" style={{ marginBottom:10 }}>{rows.length} {firstOnly ? 'first alerts' : 'alerts'} loaded · {selected.length} selected
              {loading ? ' · Loading…' : ''}</div>
            {error && <p style={{ color:'var(--error)' }}>{error}</p>}
            <div className="ma-card ma-tablewrap">
              <table className="ma-table">
                <thead><tr>
                  <th><input aria-label="Select all loaded alerts" type="checkbox" checked={!!rows.length && selected.length === rows.length}
                    onChange={e => setChecked(e.target.checked ? Object.fromEntries(rows.map(a => [a.id, true])) : {})} /></th>
                  <th>Alert time (New York)</th><th>Ticker</th><th>Session</th><th>Direction</th>
                  <th className="num">Trigger move</th><th className="num">Trigger price</th>
                  <th className="num">Last price</th><th className="num">EOD close</th><th className="num">Close vs alert</th>
                </tr></thead>
                <tbody>
                  {sorted.map(a => (
                    <tr key={a.id} className={checked[a.id] ? 'checked' : ''}>
                      <td><input type="checkbox" aria-label={'Select ' + a.ticker + ' ' + nyStamp(a.fired_at)}
                        checked={!!checked[a.id]} onChange={e => setChecked(p => ({ ...p, [a.id]: e.target.checked }))} /></td>
                      <td>{nyStamp(a.fired_at)}</td>
                      <td title={a.name} style={{ fontWeight:600 }}>{a.ticker}</td><td>{a.session}</td>
                      <td style={{ color:a.direction === 'down' ? 'var(--error)' : 'var(--success)' }}>
                        {a.direction === 'down' ? '▼ Down' : '▲ Up'}</td>
                      <td className="num">{pct(a.move_pct)}</td><td className="num">{price(a.price)}</td>
                      <td className="num">{price(a.last_price)}
                        {a.last_price_source && (
                          <small style={{ display:'block',color:'var(--text-muted)' }}>
                            {a.last_price_source === 'live'
                              ? 'Sampled ' + nyStamp(a.last_price_time)
                              : 'EOD ' + a.last_price_day}
                          </small>
                        )}
                      </td>
                      <td className="num">{price(a.eod_close)}
                        {a.eod_day && <small style={{ display:'block',color:'var(--text-muted)' }}>{a.eod_day}</small>}</td>
                      <td className="num" style={{ color:successColour(a) }}>{pct(endMove(a))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!loading && !rows.length && <div className="ma-muted" style={{ padding:20 }}>No alerts in this period.</div>}
            </div>
            {before && <button className="ma-button" style={{ marginTop:12 }} onClick={loadOlder} disabled={moreLoading}>
              {moreLoading ? 'Loading…' : 'Load older alerts'}</button>}
          </div>
        </div>
      )}
    </>
  );
}
